import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Slide,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import { tokens } from "../../../theme";

interface ContactUsToGetStartedPopupProps {
  open: boolean;
  onClose: () => void;
  onClickGotIt?: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const ContactUsToGetStartedPopup: React.FC<
  ContactUsToGetStartedPopupProps
> = ({ open, onClose, onClickGotIt }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={onClose}
      keepMounted
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.6)"
            : "0 20px 60px rgba(0,0,0,0.15)",
          backdropFilter: "blur(12px)",
        },
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          px: { xs: 3, sm: 4 },
          py: { xs: 3.5, sm: 4 },
          background: isDark
            ? "linear-gradient(135deg, #0f2d26 0%, #141c22 100%)"
            : "linear-gradient(135deg, #e8fffb 0%, #f6fffd 100%)",
        }}
      >
        {/* Close Icon */}
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: colors.grey[300],
            backgroundColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.08)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <EmailOutlinedIcon
            sx={{
              fontSize: 18,
              color: "#19C1A3",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: colors.grey[200],
              lineHeight: 1.2,
            }}
          >
            Contact Us to Get Started
          </Typography>
        </Box>

        {/* Intro Text */}
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: 1.6,
            color: colors.grey[300],
            mb: 2.5,
          }}
        >
          For Reps, Agents, Distributors, RCM/Billing Specialists,
          Manufacturers, and other industry roles, please email us to begin
          your registration process.
        </Typography>

        {/* Content Card */}
        <Box
          sx={{
            borderRadius: 2,
            px: 2.25,
            py: 2,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
            border: isDark
              ? "1px solid rgba(255,255,255,0.06)"
              : "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              color: colors.grey[200],
              mb: 0.75,
            }}
          >
            Email us at:
          </Typography>

          <Typography
            component="a"
            href="mailto:service@v3biomedical.com"
            sx={{
              display: "inline-block",
              fontSize: "14px",
              fontWeight: 700,
              color: "#19C1A3",
              cursor: "pointer",
              textDecoration: "none",
              mb: 1.75,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            service@v3biomedical.com
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 700,
              color: colors.grey[200],
              mb: 1,
            }}
          >
            Please include in your email:
          </Typography>

          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.25,
              mb: 1.5,
              color: colors.grey[400],
              "& li": {
                fontSize: "13px",
                lineHeight: 1.65,
                mb: 0.25,
              },
            }}
          >
            <li>Your full name</li>
            <li>Company name</li>
            <li>Location</li>
            <li>Your questions</li>
            <li>Any desired goals</li>
          </Box>

          <Typography sx={{ fontSize: "12px", color: colors.grey[400] }}>
            We will respond promptly!
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => {
              onClose();
              onClickGotIt && onClickGotIt();
            }}
            sx={{
              minWidth: 92,
              px: 2.25,
              py: 0.9,
              fontSize: "0.85rem",
              fontWeight: 700,
              borderRadius: 8,
              background: "linear-gradient(90deg, #19C1A3, #14B8B5)",
              textTransform: "none",
              color: "#fff",
              "&:hover": {
                filter: "brightness(1.05)",
                boxShadow: "0 8px 20px rgba(25,193,163,0.35)",
              },
            }}
          >
            Got it
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactUsToGetStartedPopup;